import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-prohance";

const FAQ_ITEMS = [
  {
    question: "How is TeamGrid different from ProHance?",
    answer: "TeamGrid is designed to help leaders understand why work happens the way it does by using AI to surface patterns, trends, and potential risks. ProHance primarily focuses on tracking effort, time, and application usage. TeamGrid emphasizes insight, clarity, and improvement rather than behavior-level tracking which is why it's considered the top ProHance Alternative by insight-led teams.",
  },
  {
    question: "What is Ask TeamGrid?",
    answer: "Ask TeamGrid is an AI-powered chat interface that allows leaders to ask practical questions like: Where is the team losing momentum? Which roles are currently overloaded? What's creating delivery risk right now? It delivers clear summaries and downloadable reports without needing spreadsheets or manual data analysis. This feature makes TeamGrid stand out as a Top ProHance Alternative for fast-moving teams.",
  },
  {
    question: "Is TeamGrid suitable for enterprise teams?",
    answer: "Yes. TeamGrid is built to scale across teams, departments, and business units. It's especially well-suited for organizations that value reliable insight over micromanagement. With a privacy-first foundation and frictionless onboarding, it's a Reliable ProHance Alternative for enterprise environments.",
  },
  {
    question: "Does TeamGrid work for remote and hybrid teams?",
    answer: "Absolutely. TeamGrid is designed to work equally well across remote, hybrid, and office-based teams. It provides consistent visibility across different working models making it one of the most adaptable and reliable ProHance alternatives for modern workforces.",
  },
  {
    question: "Is there a Free version or trial of TeamGrid?",
    answer: "Yes. TeamGrid offers a full-featured Free Trial, making it a leading choice for teams searching for a Free ProHance Alternative that delivers value immediately without a credit card or long setup process.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "How is TeamGrid different from ProHance?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid helps leaders understand why work happens the way it does using AI to surface patterns, trends, and potential risks. ProHance primarily focuses on tracking effort, time, and application usage. TeamGrid emphasizes insight, clarity, and improvement rather than behavior-level tracking.",
      },
    },
    {
      "@type": "Question",
      name: "What is Ask TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ask TeamGrid is an AI-powered chat interface that lets leaders ask practical questions about friction, overload, and delivery risk. It delivers clear summaries and downloadable reports without spreadsheets or manual data analysis.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid suitable for enterprise teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid is built to scale across teams, departments, and business units. It is designed for organizations that value reliable insight over micromanagement, with privacy-first foundations and high adoption.",
      },
    },
    {
      "@type": "Question",
      name: "Does TeamGrid work for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. TeamGrid works across remote, hybrid, and office-based teams by providing consistent visibility and decision-ready insights with a privacy-first approach.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free version or trial of TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid offers a full-featured free trial so teams can evaluate the platform quickly without long setup or friction.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "Track Less. Understand More. Drive Results — TeamGrid Is Best ProHance Alternative",
      description: "TeamGrid shows you why with AI-powered insights that reveal patterns, risks, and opportunities to improve. High-performing teams trust TeamGrid as a privacy-first ProHance alternative built for clarity and fast action.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://www.prohance.net/#prohance-app" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs ProHance", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      description: "TeamGrid is an AI-powered workforce intelligence platform that helps leaders understand work patterns, risks, and opportunities—without surveillance—so teams can improve with clarity and trust.",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      featureList: [
        "Workforce intelligence and decision support",
        "AI-powered insights that explain trends and bottlenecks automatically",
        "Ask TeamGrid (conversational AI) for instant answers and downloadable reports",
        "Privacy-first design with transparency and trust",
        "Insight-led visibility instead of usage-led monitoring",
        "Real-time team dashboards focused on clarity and improvement",
        "Daily, hourly, and weekly summaries",
        "One-click, shareable, presentation-ready reports",
        "Organizational Intelligence Engine surfaces overload, risks, and blockers",
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://www.prohance.net/#prohance-app",
      name: "ProHance",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, Web",
      description: "ProHance is a workforce productivity platform focused on measuring time, system usage, and effort metrics with dashboards and configurable reports.",
      url: "https://www.prohance.net/",
      publisher: { "@type": "Organization", name: "ProHance", url: "https://www.prohance.net/" },
      featureList: [
        "Time and usage efficiency metrics",
        "Productivity dashboards and effort tracking",
        "Tracking features and desktop activity metrics",
        "Custom reports and dashboards",
        "Exports such as Excel and PDF",
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs ProHance - Feature Comparison",
      description: "Comparison of TeamGrid and ProHance across philosophy, AI insights, conversational AI, privacy approach, visibility model, dashboards, organizational intelligence, summaries, and exports.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://www.prohance.net/#prohance-app" } },
      ],
    },
  ],
};

const PROHANCE_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Understanding work patterns", competitor: "Measuring time and usage efficiency" },
  { feature: "AI-Powered Insights", teamgrid: "Explains trends and bottlenecks automatically", competitor: "Primarily usage metrics and manual reporting" },
  { feature: "Ask TeamGrid (AI Chat)", teamgrid: "Ask questions and get instant answers", competitor: "Not available" },
  { feature: "Privacy-Oriented Design", teamgrid: "Designed with transparency and trust in mind", competitor: "Includes tracking features and desktop activity metrics" },
  { feature: "Approach to Visibility", teamgrid: "Insight-led by default", competitor: "Activity and usage-led by default" },
  { feature: "Dashboards", teamgrid: "Summaries focused on clarity and improvement", competitor: "Focused on productivity metrics and effort tracking" },
  { feature: "Organizational Insights", teamgrid: "Highlights potential risks and improvement opportunities", competitor: "Requires manual analysis of collected data" },
  { feature: "Work Summaries", teamgrid: "Daily, hourly, and weekly summaries", competitor: "Custom reports and dashboards" },
  { feature: "Export & Sharing", teamgrid: "One-click, shareable reports", competitor: "Multi-format exports such as Excel and PDF" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Visibility comes from analyzing work patterns, not micromanagement",
  "Leaders get meaningful insights, while teams feel respected",
  "Teams adopt the platform faster, with minimal friction",
  "This is why TeamGrid is seen as the Top ProHance Alternative by forward-thinking companies",
];

const DATA_DECISIONS_PROHANCE_BULLETS = [
  "Visibility is based on system usage, logins, and application metrics",
  "May feel overly observational, which affects team trust",
  "Custom reporting takes time and ongoing configuration",
];

const DATA_DECISIONS_2_TEAMGRID_BULLETS = [
  "AI identifies overload, risks and blockers in real time",
  "Daily and weekly summaries reveal focus points",
  "Teams act faster and more consistently with insight-driven context",
];

const DATA_DECISIONS_2_PROHANCE_BULLETS = [
  "Strong focus on time and usage metrics",
  "Managers need to manually analyze trends",
  "Action depends on user interpretation of raw data",
];

const PROHANCE_WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid (AI chat for reports and insights)",
  "Organizational Intelligence Engine",
  "Real-time dashboards",
  "Daily, hourly, and weekly summaries",
  "One-click, presentation ready reports",
  "Designed to be a surveillance free ProHance alternative",
  "Works for remote, hybrid and in-office teams",
  "Easy onboarding and high adoption rates",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid – Your AI Workforce Assistant",
    intro: "Leaders can ask simple, practical questions like:",
    bullets: [
      "Where is the team hitting friction?",
      "Which functions are showing signs of overload?",
      "What may be affecting delivery or performance?",
    ],
    closing: "TeamGrid provides direct, downloadable insights without needing spreadsheets or data modeling.",
  },
  {
    title: "Organizational Intelligence",
    intro: "TeamGrid helps surface:",
    bullets: [
      "Workflow bottlenecks",
      "Overload and fatigue signals",
      "Friction between teams or processes",
      "Patterns that affect delivery timelines",
    ],
    closing: "All identified automatically, without constant monitoring.",
  },
];

const ProHanceComparison = () => (
  <ComparisonPageTemplate
    seoTitle="Best ProHance Alternative | AI-Powered Insights with TeamGrid"
    seoDescription="Looking for a Best ProHance alternative? TeamGrid delivers AI-powered workforce insights without surveillance. Built for trust, clarity, and faster decisions."
    seoCanonical={CANONICAL_URL}
    jsonLdGraph={JSON_LD_GRAPH}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleLine1="Track Less. Understand More. Drive Results"
    heroTitleLine2="TeamGrid Is Best ProHance Alternative"
    heroTitleLine2Teal
    heroTitleLine3={null}
    heroTitleLine4={null}
    heroDescription="TeamGrid shows you why with AI-powered insights that reveal patterns, risks, and opportunities to improve. That is why high-performing teams trust TeamGrid as the best ProHance alternative."
    competitorName="ProHance"
    competitorLogoUrl="/Background_prohance.webp"
    comparisonTableRows={PROHANCE_TABLE_ROWS}
    dataDecisionsHeading="Use Case 1: Understanding Work Without Watching Every Click"
    dataDecisionsSubtitle="Why Many Organizations Choose the Best ProHance Alternative for Insight-Driven Visibility"
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_PROHANCE_BULLETS}
    dataDecisionsCompetitorCardTitle="ProHance"
    dataDecisions2Heading="Use Case 2: Turning Work Data Into Actionable Decisions"
    dataDecisions2Subtitle="Why Teams Look for the Reliable ProHance Alternative for Decision Support"
    dataDecisions2TeamGridBullets={DATA_DECISIONS_2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS_2_PROHANCE_BULLETS}
    dataDecisions2CompetitorCardTitle="ProHance"
    keyFeaturesTitle="Why Teams Choose TeamGrid Over ProHance"
    keyFeaturesSubtitle="Ask TeamGrid – Your AI Workforce Assistant"
    keyFeatureCards={KEY_FEATURE_CARDS}
    whatYouGetItems={PROHANCE_WHAT_YOU_GET_ITEMS}
    outcomesTitle="Performance Outcomes Teams See"
    outcomesImageSrc="/Section_prohance_1.webp"
    trustedBadges={[
      "AI-driven workforce intelligence platform",
      "Ask TeamGrid: Get answers, not just data",
      "Understand flow, focus, and friction",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. ProHance."
  />
);

export default ProHanceComparison;
